<template>
  <section class="py-12">
    <div class="container-wrap">
      <h2 class="text-h1 mb-6">{{ t('title') }}</h2>
      <div class="tablet:flex tablet:items-center">
        <div
          class="relative mb-2 w-full tablet:mb-0 tablet:mr-3 tablet:w-[200px]"
        >
          <FieldDropdown
            v-if="!isMobile"
            id="news_select_type"
            :model-value="routeTypeCodes"
            :title="t('select')"
            :options="newsTypes"
            @update:model-value="handleTypeChange"
          />
          <FieldDropdownTags
            v-else
            id="news_select_type"
            :model-value="routeTypeCodes"
            :title="t('select')"
            :options="newsTypes"
            @update:model-value="handleTypeChange"
          />
        </div>
        <div class="relative mb-4 w-full tablet:mb-0 tablet:w-[280px]">
          <FieldInputSearch
            id="news_search-value"
            v-model="inputValue"
            name="Search Keyword"
            icon-position="prefix"
            :clearable="true"
            :enterable="true"
            :searchable="true"
            :placeholder="tGlobal('form.search')"
            @search="handleSearch"
            @clear="handleClearSearch"
          />
        </div>
      </div>
    </div>

    <div
      v-if="!isMobile"
      class="my-3 h-12 w-full py-1.5"
      :class="routeTypeCodes.length ? 'bg-blue-400/10' : 'bg-white'"
    >
      <div class="container-wrap">
        <ul class="flex items-center">
          <li
            v-for="item in contextTypeCodes"
            :key="item.code"
            class="relative mr-2 bg-white p-1.5 pr-8"
          >
            <div class="text-blue-400">{{ item.name }}</div>
            <button
              class="absolute right-1 top-2.5 mx-1 h-5 w-5 rounded-full bg-blue-400"
              @click="handleRemoveType(item.code)"
            >
              <IconClose class="h-5 w-5 text-white" />
            </button>
          </li>
        </ul>
      </div>
    </div>

    <LoaderBounce v-if="isLoading" class="py-52" />
    <div v-if="!isLoading" class="container-wrap">
      <ul v-if="resources.items.length" class="column-221">
        <li v-for="item in resources.items" :key="hash(item)">
          <CardInformation
            :title="item.title"
            :caption="item.releaseDate"
            :description="item.description"
            :image-url="item.imageUrl"
            :to="item.pageUrl"
            :button-name="t('item-button')"
          />
        </li>
      </ul>

      <NoSearchResult
        v-if="resources.items.length <= 0 && routeKeyword.length >= 1"
        class="my-10"
        :keyword="routeKeyword"
      />

      <ThePagination
        class="my-6"
        :page-items="paginationItems"
        :page-index="resources.pageIndex"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
  import hash from 'object-hash';
  import type { LocationQueryRaw, RouteLocationRaw } from 'vue-router';

  import type { IPageItem } from '@/components/Shared/ThePagination.vue';

  const route = useRoute();
  const router = useRouter();

  const { t, locale } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });
  const { isMobile } = useScreen();

  const newsStore = useNewsStore();

  const { resources, totalPage, newsTypes, newsTypesCode } =
    storeToRefs(newsStore);

  // 搜尋關鍵字
  const routeKeyword = computed((): string =>
    typeof route.query.keyword === 'string' ? route.query.keyword.trim() : ''
  );

  // 頁碼
  const routePageIndex = computed((): number => Number(route.query.page) || 1);

  // 選擇的類型
  const routeTypeCodes = computed((): string[] => {
    const queryType = route.query.type;

    if (queryType) {
      if (Array.isArray(queryType)) {
        return queryType.filter((item): item is string => {
          return typeof item === 'string' && newsTypesCode.value.includes(item);
        });
      } else if (
        typeof queryType === 'string' &&
        newsTypesCode.value.includes(queryType)
      ) {
        return [queryType];
      }
    }
    return [];
  });

  const { status } = await newsStore.USE_GET_RESOURCES(
    routeKeyword,
    routePageIndex,
    routeTypeCodes
  );

  const inputValue = ref<string>(routeKeyword.value);

  const isLoading = computed((): boolean => status.value === 'pending');

  const paginationItems = computed((): IPageItem[] => {
    const result = [];
    for (let i = 0; i < totalPage.value; i++) {
      result.push({
        pageNumber: i + 1,
        pageLink: providePageLink(i + 1),
      });
    }
    return result;
  });

  const typeNameMap = computed(
    (): Map<string, string> =>
      new Map(newsTypes.value.map(({ code, name }) => [code, name]))
  );

  const contextTypeCodes = computed((): { code: string; name: string }[] =>
    routeTypeCodes.value.map((code) => ({
      code,
      name: typeNameMap.value.get(code) ?? '',
    }))
  );

  // 更新路由的 query 參數

  const updateRouteQuery = (query: LocationQueryRaw): void => {
    const cleanQuery = Object.fromEntries(
      Object.entries(query).filter(
        ([key, value]) =>
          value !== undefined && !(key === 'page' && Number(value) === 1)
      )
    );
    router.replace({
      path: route.path,
      query: cleanQuery,
    });
  };

  // 按下搜尋按鈕

  const handleSearch = (): void => {
    updateRouteQuery({
      page: 1,
      type: routeTypeCodes.value.length ? routeTypeCodes.value : undefined,
      keyword: inputValue.value || undefined,
    });
  };

  // 按下清除搜尋按鈕

  const handleClearSearch = (): void => {
    inputValue.value = '';

    updateRouteQuery({
      page: 1,
      type: routeTypeCodes.value.length ? routeTypeCodes.value : undefined,
      keyword: undefined,
    });
  };

  // 調整類型選擇

  const handleTypeChange = (newTypes: string[]): void => {
    updateRouteQuery({
      page: 1,
      type: newTypes.length ? newTypes : undefined,
      keyword: routeKeyword.value || undefined,
    });
  };

  // 移除已選擇的類型

  const handleRemoveType = (type: string): void => {
    const newTypes = routeTypeCodes.value.filter(
      (item: string) => item !== type
    );
    updateRouteQuery({
      page: 1,
      type: newTypes.length ? newTypes : undefined,
      keyword: routeKeyword.value || undefined,
    });
  };

  const providePageLink = (num: number): RouteLocationRaw => {
    const { path } = route;
    const query: LocationQueryRaw = {};

    if (num !== 1) {
      query.page = `${num}`;
    }
    if (routeKeyword.value !== '') {
      query.keyword = routeKeyword.value;
    }
    if (routeTypeCodes.value.length) {
      query.type = routeTypeCodes.value;
    }

    return { path, query };
  };

  watch([isMobile, locale], () => {
    updateRouteQuery({
      page: 1,
      type: undefined,
      keyword: undefined,
    });
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "The Latest",
    "select": "Select Type",
    "item-button": "Learn more"
  },
  "en-jp": {
    "title": "The Latest",
    "select": "Select Type",
    "item-button": "Learn more"
  },
  "en-tw": {
    "title": "The Latest",
    "select": "Select Type",
    "item-button": "Learn more"
  },
  "en-kr": {
    "title": "The Latest",
    "select": "Select Type",
    "item-button": "Learn more"
  },
  "en-americas": {
    "title": "The Latest",
    "select": "Select Type",
    "item-button": "Learn more"
  },
  "en-asia-pacific": {
    "title": "The Latest",
    "select": "Select Type",
    "item-button": "Learn more"
  },
  "en-emea": {
    "title": "The Latest",
    "select": "Select Type",
    "item-button": "Learn more"
  },
  "ja-jp": {
    "title": "インフォメーション・エクスプレス",
    "select": "タイプを選択",
    "item-button": "詳細検索"
  },
  "zh-cn": {
    "title": "资讯速递",
    "select": "选择类型",
    "item-button": "了解更多"
  },
  "zh-tw": {
    "title": "資訊速遞",
    "select": "選擇類型",
    "item-button": "了解更多"
  }
}
</i18n>
